/**
 * Hook que mantiene una conexión WebSocket al chat realtime.
 * - Auto-reconnect con backoff
 * - Keepalive ping cada 25s
 * - Llama onMessage(payload) para cada evento del server
 *
 * Auth: el token NO va en query string (eso quedaba escrito en logs nginx).
 * Después del onopen mandamos {"type":"auth","token":"..."} y esperamos el
 * "ready" del server antes de considerar la conexión utilizable.
 *
 * Si WS falla por completo, el ChatScreen sigue funcionando porque tiene
 * polling como fallback.
 */
import { useEffect, useRef } from "react";
import { apiBaseUrl, getToken } from "../api/client";

export interface ChatWsEvent {
  type: "message" | "trade_update" | "pong" | "ready" | string;
  [key: string]: unknown;
}

export function useChatSocket(onMessage: (event: ChatWsEvent) => void): void {
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    const token = getToken();
    if (!token) return;

    const base = apiBaseUrl().replace(/^http/, "ws");
    const url = `${base}/ws/chat`;

    let ws: WebSocket | null = null;
    let pingInterval: ReturnType<typeof setInterval> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }

      ws.onopen = () => {
        // Mandamos auth como primer frame. El server espera 5s, sino cierra.
        try {
          ws?.send(JSON.stringify({ type: "auth", token }));
        } catch {
          /* el onclose dispara reconnect */
        }
      };

      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "ready") {
            // Auth OK. Reset attempt + arrancar keepalive.
            attempt = 0;
            pingInterval = setInterval(() => {
              try { ws?.send("ping"); } catch { /* ignore */ }
            }, 25_000);
            return;
          }
          handlerRef.current(data);
        } catch {
          /* ignore */
        }
      };

      ws.onclose = () => {
        if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
        scheduleReconnect();
      };

      ws.onerror = () => {
        try { ws?.close(); } catch { /* ignore */ }
      };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      attempt += 1;
      const delay = Math.min(30_000, 1000 * Math.pow(2, Math.min(attempt, 5)));
      reconnectTimer = setTimeout(connect, delay);
    };

    connect();

    return () => {
      cancelled = true;
      if (pingInterval) clearInterval(pingInterval);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, []);
}
